<template>
  <el-dialog
    title="生成/更新统计"
    :visible.sync="dialogVisible"
    width="580px"
    :close-on-click-modal="false"
    custom-class="generate-report-dialog"
    @open="onOpen"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="110px" size="small">
      <!-- 报告类型 -->
      <el-form-item label="报告类型" prop="reportType">
        <el-radio-group v-model="form.reportType" @change="onTypeChange">
          <el-radio :label="1">月度报告</el-radio>
          <el-radio :label="2">季度报告</el-radio>
          <el-radio :label="3">年度报告</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 年度（始终显示） -->
      <el-form-item label="年度" prop="year">
        <el-select v-model="form.year" @change="onYearChange" style="width: 130px">
          <el-option v-for="y in yearOptions" :key="y" :label="y + '年'" :value="y" />
        </el-select>
      </el-form-item>

      <!-- 月度模式：月份 -->
      <el-form-item v-if="form.reportType === 1" label="月份" prop="month">
        <el-select v-model="form.month" @change="onMonthChange" style="width: 130px" placeholder="请选择月份">
          <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
        </el-select>
        <div class="hint-text">月度报告按当前选中波次生成（下方"波次"必选）</div>
      </el-form-item>

      <!-- 文档 50 P0-01 / 文档 52 P2-01：月度模式必须选择波次。
           默认按所选年月过滤，但提供"全部波次"切换，避免跨月波次或缺 EndTime 的合法波次被过滤掉。 -->
      <el-form-item v-if="form.reportType === 1" label="波次" prop="waveId">
        <div class="wave-picker">
          <el-select v-model="form.waveId" style="width: 280px" placeholder="请选择波次"
                     :loading="loadingWaves" filterable>
            <el-option v-for="w in waveOptionsForDialog" :key="w.id" :label="w.name" :value="w.id">
              <span style="float:left">{{ w.name }}</span>
              <span style="float:right;color:#94a3b8;font-size:12px">{{ w.timeLabel || '' }}</span>
            </el-option>
          </el-select>
          <!-- 文档 54 跟进：扁平化过滤切换，与单店报告页风格一致（蓝色激活态） -->
          <div class="wave-filter-toggle">
            <button type="button"
                    :class="['wave-filter-btn', { active: form.waveFilterMode === 'month' }]"
                    @click="setWaveFilterMode('month')">当前月波次</button>
            <button type="button"
                    :class="['wave-filter-btn', { active: form.waveFilterMode === 'all' }]"
                    @click="setWaveFilterMode('all')">全部波次</button>
          </div>
        </div>
        <div class="hint-text">
          <template v-if="form.waveFilterMode === 'month' && waveOptionsForCurrentMonth.length === 0 && (allWaves || []).length > 0">
            <span style="color:#b45309;">
              <i class="el-icon-warning-outline"></i>
              当前月（{{ form.year }}年{{ form.month || '?' }}月）没有结束时间在该月的波次。点击「全部波次」查看项目下所有波次。
            </span>
          </template>
          <template v-else-if="(allWaves || []).length === 0 && !loadingWaves">
            <span style="color:#b45309;">
              <i class="el-icon-warning-outline"></i>
              当前项目下暂无波次，请先在「波次管理」中创建波次。
            </span>
          </template>
          <template v-else>
            默认仅显示所选月份内（按结束时间）的波次；如波次跨月或缺结束时间，请切换"全部波次"。
          </template>
        </div>
      </el-form-item>

      <!-- 文档 48-§5.2：审核级别（仅月度报告，季度/年度由源快照继承） -->
      <el-form-item v-if="form.reportType === 1" label="审核级别" prop="reviewLevel">
        <el-radio-group v-model="form.reviewLevel">
          <el-radio :label="1">一审</el-radio>
          <el-radio :label="2">二审</el-radio>
          <el-radio :label="3">三审</el-radio>
        </el-radio-group>
        <div class="hint-text">
          选哪一级就只读取该级别审核结果跑分；缺该级别审核的题项标记为「待审」，不计入得分与落实率。
        </div>
      </el-form-item>

      <!-- 季度模式：季度 -->
      <el-form-item v-if="form.reportType === 2" label="季度" prop="quarter">
        <el-select v-model="form.quarter" @change="onQuarterChange" style="width: 130px" placeholder="请选择季度">
          <el-option v-for="q in 4" :key="q" :label="'Q' + q + ' (' + quarterMonths(q) + '月)'" :value="q" />
        </el-select>
      </el-form-item>

      <!-- 季度模式：合并月度报告源 -->
      <el-form-item v-if="form.reportType === 2" label="合并月度报告" prop="selectedSnapshotIds">
        <div v-if="loadingSources" class="hint-text">加载可用月度报告...</div>
        <div v-else-if="filteredSources.length === 0" class="empty-tip">
          所选季度下暂无月度报告，请先在该季度的每个月生成月度报告
        </div>
        <el-checkbox-group v-else v-model="form.selectedSnapshotIds">
          <div v-for="s in filteredSources" :key="s.snapshotId" class="source-item">
            <el-checkbox :label="s.snapshotId">
              {{ s.periodLabel }}
              <!-- 文档 52 P1-03：审核级别标签 + 波次名，避免同名期次混淆 -->
              <el-tag size="mini" :type="reviewLevelTagType(s.reviewLevel)" effect="plain">
                {{ reviewLevelLabel(s.reviewLevel) }}
              </el-tag>
              <el-tag v-if="s.waveName" size="mini" type="info" effect="plain">{{ s.waveName }}</el-tag>
              <el-tag size="mini" :type="s.isPublished ? 'success' : 'info'" effect="plain">
                {{ s.isPublished ? '已发布' : '草稿' }}
              </el-tag>
              <span class="source-meta">{{ s.storeCount }} 门店</span>
              <span v-if="s.scoringRuleCode" class="source-meta">规则: {{ scoringRuleLabel(s.scoringRuleCode) }}</span>
            </el-checkbox>
          </div>
        </el-checkbox-group>
      </el-form-item>

      <!-- 年度模式：合并方式 -->
      <el-form-item v-if="form.reportType === 3" label="合并方式" prop="mergeMode">
        <el-radio-group v-model="form.mergeMode" @change="onMergeModeChange">
          <el-radio :label="1">按月度合并</el-radio>
          <el-radio :label="2">按季度合并</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 年度模式：合并源 -->
      <el-form-item v-if="form.reportType === 3" label="合并报告" prop="selectedSnapshotIds">
        <div v-if="loadingSources" class="hint-text">加载可用报告...</div>
        <div v-else-if="filteredSources.length === 0" class="empty-tip">
          所选年度下暂无{{ form.mergeMode === 1 ? '月度' : '季度' }}报告
        </div>
        <el-checkbox-group v-else v-model="form.selectedSnapshotIds">
          <div v-for="s in filteredSources" :key="s.snapshotId" class="source-item">
            <el-checkbox :label="s.snapshotId">
              {{ s.periodLabel }}
              <!-- 文档 52 P1-03：审核级别标签 + 波次名，避免同名期次混淆 -->
              <el-tag size="mini" :type="reviewLevelTagType(s.reviewLevel)" effect="plain">
                {{ reviewLevelLabel(s.reviewLevel) }}
              </el-tag>
              <el-tag v-if="s.waveName" size="mini" type="info" effect="plain">{{ s.waveName }}</el-tag>
              <el-tag size="mini" :type="s.isPublished ? 'success' : 'info'" effect="plain">
                {{ s.isPublished ? '已发布' : '草稿' }}
              </el-tag>
              <span class="source-meta">{{ s.storeCount }} 门店</span>
              <span v-if="s.scoringRuleCode" class="source-meta">规则: {{ scoringRuleLabel(s.scoringRuleCode) }}</span>
            </el-checkbox>
          </div>
        </el-checkbox-group>
        <div v-if="filteredSources.length > 0" class="select-actions">
          <el-button type="text" size="mini" @click="selectAll">全选</el-button>
          <el-button type="text" size="mini" @click="clearSelection">清空</el-button>
        </div>
      </el-form-item>

      <div class="dialog-tip">报告生成后保存为快照，可重复生成覆盖。</div>
    </el-form>

    <div slot="footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button size="small" type="primary" :loading="generating" @click="handleConfirm">生成</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'index/axios'

const RULE_LABELS = {
  simple: '原始累加',
  percentage: '百分制',
  bonusNormalized: '加分题百分制',
  custom: '自定义'
}

export default {
  name: 'GenerateReportDialog',
  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: [Number, String], required: true },
    formKey: { type: String, required: true },
    formName: { type: String, default: '' }
  },
  data () {
    return {
      form: {
        reportType: 1,
        year: new Date().getFullYear(),
        month: null,
        quarter: null,
        mergeMode: 1,
        selectedSnapshotIds: [],
        // 文档 48-§5.2：审核级别。默认 3（终审），用户可改
        reviewLevel: 3,
        // 文档 50 P0-01：月度模式选中的波次 ID
        waveId: null,
        // 文档 52 P2-01：波次下拉过滤模式 month=按当前月 / all=全部波次
        waveFilterMode: 'month'
      },
      availableSources: [],
      loadingSources: false,
      generating: false,
      // 文档 50 P0-01：项目下所有波次（含起止时间），用于生成弹窗里的"波次"下拉
      allWaves: [],
      loadingWaves: false,
      rules: {
        reportType: [{ required: true, message: '请选择报告类型', trigger: 'change' }],
        year: [{ required: true, message: '请选择年度', trigger: 'change' }],
        month: [{ required: true, message: '请选择月份', trigger: 'change' }],
        quarter: [{ required: true, message: '请选择季度', trigger: 'change' }],
        mergeMode: [{ required: true, message: '请选择合并方式', trigger: 'change' }],
        reviewLevel: [{
          validator: (rule, value, callback) => {
            if (this.form.reportType === 1 && !(value === 1 || value === 2 || value === 3)) {
              callback(new Error('请选择审核级别'))
            } else { callback() }
          },
          trigger: 'change'
        }],
        waveId: [{
          validator: (rule, value, callback) => {
            if (this.form.reportType === 1 && !(value > 0)) {
              callback(new Error('请选择波次'))
            } else { callback() }
          },
          trigger: 'change'
        }],
        selectedSnapshotIds: [{
          validator: (rule, value, callback) => {
            if (this.form.reportType >= 2 && (!value || value.length === 0)) {
              callback(new Error('请至少选择一个报告作为合并源'))
            } else { callback() }
          },
          trigger: 'change'
        }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.visible },
      set (v) { this.$emit('update:visible', v) }
    },
    yearOptions () {
      const cur = new Date().getFullYear()
      return [cur - 2, cur - 1, cur, cur + 1]
    },
    filteredSources () {
      if (this.form.reportType === 2 && this.form.quarter) {
        const qStart = (this.form.quarter - 1) * 3 + 1
        const qEnd = qStart + 2
        return this.availableSources.filter(s => s.month && s.month >= qStart && s.month <= qEnd)
      }
      return this.availableSources
    },
    /**
     * 文档 50 P0-01：根据当前 (Year, Month) 过滤出该月内的波次。
     * 取波次的 EndTime 落在该月的（兼容无 EndTime 的取 StartTime）
     */
    waveOptionsForCurrentMonth () {
      if (!this.form.year || !this.form.month) return []
      const y = this.form.year
      const m = this.form.month
      return (this.allWaves || []).filter(w => {
        const ref = w.endTime || w.startTime
        if (!ref) return false
        const d = new Date(ref)
        return d.getFullYear() === y && (d.getMonth() + 1) === m
      })
    },
    /**
     * 文档 52 P2-01：波次下拉的最终选项。
     *   - month 模式：按当前所选年月过滤（旧行为）
     *   - all 模式：项目下全部波次（含跨月或无结束时间）
     */
    waveOptionsForDialog () {
      if (this.form.waveFilterMode === 'all') return this.allWaves || []
      return this.waveOptionsForCurrentMonth
    }
  },
  methods: {
    onOpen () {
      this.form = {
        reportType: 1,
        year: new Date().getFullYear(),
        month: null,
        quarter: null,
        mergeMode: 1,
        selectedSnapshotIds: [],
        reviewLevel: 3,
        waveId: null,
        waveFilterMode: 'month'
      }
      this.availableSources = []
      // 文档 50 P0-01：每次打开弹窗都拉取项目波次
      this.loadProjectWaves()
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    /** 文档 50 P0-01 + 文档 52 P0-02：拉取项目下波次（含起止时间），供月度模式过滤展示。
     *  后端 /WaveManager/wave/list 返回的分页字段是 PascalCase 的 Rows，旧版本只读 lower-case 会拿不到列表，
     *  导致波次下拉空、月度生成被卡住。这里统一兼容大小写 + 常见分页字段。 */
    async loadProjectWaves () {
      if (!this.projectId) return
      this.loadingWaves = true
      try {
        const res = await axios.post('/WaveManager/wave/list', { ProjectId: this.projectId, Page: 1, PageSize: 9999 })
        const wrap = (res && res.data) || res || {}
        const data = wrap.data || wrap
        // 文档 52 P0-02：兼容 Rows / rows / list / records / 直接数组
        const list = Array.isArray(data)
          ? data
          : (data.Rows || data.rows || data.list || data.records || data.Data?.Rows || [])
        this.allWaves = list.map(w => ({
          id: w.Id || w.id,
          name: w.Name || w.name,
          startTime: w.StartTime || w.startTime,
          endTime: w.EndTime || w.endTime,
          timeLabel: this.formatRange(w.StartTime || w.startTime, w.EndTime || w.endTime)
        })).filter(w => w.id)
      } catch (e) {
        console.warn('[GenerateReportDialog] load waves failed', e)
        this.allWaves = []
      } finally {
        this.loadingWaves = false
      }
    },
    formatRange (s, e) {
      const f = v => {
        if (!v) return ''
        const d = new Date(v)
        if (isNaN(d.getTime())) return ''
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
      const fs = f(s), fe = f(e)
      if (fs && fe) return `${fs} ~ ${fe}`
      return fs || fe || ''
    },
    onMonthChange () {
      // 月份切换时清空已选波次，避免选了不属于该月的波次
      this.form.waveId = null
    },
    onWaveFilterModeChange () {
      // 切换"全部波次/当前月波次"时，如果当前已选波次不在新过滤集合里就清空
      const list = this.waveOptionsForDialog
      if (this.form.waveId && !list.find(w => w.id === this.form.waveId)) {
        this.form.waveId = null
      }
    },
    /** 文档 54 跟进：扁平化按钮切换波次过滤模式 */
    setWaveFilterMode (mode) {
      if (this.form.waveFilterMode === mode) return
      this.form.waveFilterMode = mode
      this.onWaveFilterModeChange()
    },
    onTypeChange (type) {
      this.form.month = null
      this.form.quarter = null
      this.form.selectedSnapshotIds = []
      this.form.waveId = null
      if (type >= 2) this.loadAvailableSources()
    },
    onYearChange () {
      this.form.month = null
      this.form.quarter = null
      this.form.selectedSnapshotIds = []
      if (this.form.reportType >= 2) this.loadAvailableSources()
    },
    onQuarterChange () {
      this.form.selectedSnapshotIds = []
    },
    onMergeModeChange () {
      this.form.selectedSnapshotIds = []
      this.loadAvailableSources()
    },
    quarterMonths (q) {
      const start = (q - 1) * 3 + 1
      return `${start}-${start + 2}`
    },
    scoringRuleLabel (code) {
      return RULE_LABELS[code] || code
    },
    /** 文档 52 P1-03：审核级别标签文本 */
    reviewLevelLabel (lvl) {
      if (lvl === 1) return '一审'
      if (lvl === 2) return '二审'
      if (lvl === 3) return '三审'
      return '历史口径'
    },
    /** 文档 52 P1-03：审核级别标签颜色 */
    reviewLevelTagType (lvl) {
      if (lvl === 3) return 'success'
      if (lvl === 2) return 'warning'
      if (lvl === 1) return 'info'
      return 'info'
    },
    selectAll () {
      this.form.selectedSnapshotIds = this.filteredSources.map(s => s.snapshotId)
    },
    clearSelection () {
      this.form.selectedSnapshotIds = []
    },
    async loadAvailableSources () {
      if (this.form.reportType < 2) return
      const periodType = this.form.reportType === 3 && this.form.mergeMode === 2 ? 2 : 1
      this.loadingSources = true
      try {
        const res = await axios.post('/Report/availableSources', {
          ProjectId: this.projectId,
          FormKey: this.formKey,
          Year: this.form.year,
          PeriodType: periodType
        })
        // v1.5 修复 P2-02：兼容 axios wrapper 的多层结构
        // 可能形态：{ items }、{ data: { items } }、{ data: { data: { items } } }
        const wrapper = (res && res.data) || res || {}
        const items = wrapper.items ||
          (wrapper.data && wrapper.data.items) ||
          (wrapper.data && wrapper.data.data && wrapper.data.data.items) ||
          []
        this.availableSources = items
      } catch (e) {
        this.availableSources = []
        this.$message.error('加载可用报告失败')
      } finally {
        this.loadingSources = false
      }
    },
    async handleConfirm () {
      try {
        await this.$refs.form.validate()
      } catch {
        return
      }

      // 前端校验：合并源算分规则一致
      if (this.form.reportType >= 2) {
        const codes = [...new Set(
          this.availableSources
            .filter(s => this.form.selectedSnapshotIds.includes(s.snapshotId))
            .map(s => s.scoringRuleCode)
            .filter(Boolean)
        )]
        if (codes.length > 1) {
          this.$message.error('选中的源报告算分规则不一致，无法合并')
          return
        }
      }

      const payload = {
        ProjectId: this.projectId,
        FormKey: this.formKey,
        ReportType: this.form.reportType,
        Year: this.form.year,
        Month: this.form.reportType === 1 ? this.form.month : null,
        Quarter: this.form.reportType === 2 ? this.form.quarter : null,
        MergeMode: this.form.reportType === 3 ? this.form.mergeMode : 1,
        SourceMonthlySnapshotIds: this.form.reportType === 2 ? this.form.selectedSnapshotIds : null,
        SourceSnapshotIds: this.form.reportType === 3 ? this.form.selectedSnapshotIds : null,
        // 文档 48-§5.2：仅月度模式传 ReviewLevel；季度/年度由源快照继承
        ReviewLevel: this.form.reportType === 1 ? this.form.reviewLevel : null,
        // 文档 50 P0-01：月度模式必传 WaveId
        WaveId: this.form.reportType === 1 ? this.form.waveId : null
      }

      this.generating = true
      try {
        const res = await axios.post('/Report/generateV2', payload)
        const ok = (res && (res.errcode === 0 || (res.data && res.data.errcode === 0)))
        if (ok) {
          this.$message.success((res.message || (res.data && res.data.message)) || '报告已生成')
          this.dialogVisible = false
          this.$emit('success', {
            reportType: this.form.reportType,
            year: this.form.year,
            month: this.form.month,
            quarter: this.form.quarter,
            snapshotId: (res.snapshotId || (res.data && res.data.snapshotId)),
            periodKey: (res.periodKey || (res.data && res.data.periodKey)),
            periodLabel: (res.periodLabel || (res.data && res.data.periodLabel)),
            // 文档 50 P0-01/P0-02：把 WaveId / ReviewLevel 一并回传给父组件，方便后续刷新查询命中正确快照
            waveId: this.form.reportType === 1 ? this.form.waveId : null,
            reviewLevel: this.form.reportType === 1 ? this.form.reviewLevel : null
          })
        } else {
          const msg = (res && (res.message || (res.data && res.data.message))) || '生成失败'
          this.$message.error(msg)
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '生成失败'
        this.$message.error(msg)
      } finally {
        this.generating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hint-text {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}
.empty-tip {
  color: #94a3b8;
  font-size: 12px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  padding: 8px 12px;
  border-radius: 4px;
}
.source-item {
  margin-bottom: 6px;
  padding: 6px 8px;
  border: 1px solid #eef2f6;
  border-radius: 4px;
  background: #fafbfd;
  ::v-deep .el-checkbox__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}
.source-meta {
  color: #64748b;
  font-size: 12px;
}
.select-actions {
  margin-top: 4px;
}
.dialog-tip {
  color: #94a3b8;
  font-size: 12px;
  margin-top: 8px;
  padding-left: 110px;
}

/* 文档 54 跟进：波次选择器扁平化样式 */
.wave-picker {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.wave-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.wave-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
  background: #fff;
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 500;
  line-height: 26px;
  cursor: pointer;
  outline: none;
  transition: background-color .16s ease, color .16s ease, border-color .16s ease, box-shadow .16s ease;
  user-select: none;
}
.wave-filter-btn:hover:not(.active) {
  background: #f6f8fb;
  color: #1f2937;
  border-color: #c3cfe5;
}
.wave-filter-btn.active {
  background: #3f65d8;
  color: #fff;
  border-color: #3f65d8;
  box-shadow: 0 2px 6px rgba(63, 101, 216, .18);
}
</style>

<style lang="scss">
/* el-dialog 渲染到 body，需要非 scoped 样式 */
.generate-report-dialog {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .12);

  .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #eef2f6;
    background: #fff;

    .el-dialog__title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;
      line-height: 22px;
    }
    .el-dialog__headerbtn {
      top: 18px;
      right: 18px;
      .el-dialog__close {
        color: #94a3b8;
        font-size: 18px;
      }
      &:hover .el-dialog__close { color: #475569; }
    }
  }

  .el-dialog__body {
    padding: 20px 24px 8px;
    color: #334155;
    font-size: 13px;
  }

  .el-dialog__footer {
    padding: 12px 20px 16px;
    border-top: 1px solid #eef2f6;
    background: #fff;

    .el-button + .el-button { margin-left: 8px; }
    .el-button--default {
      background: #fff;
      border-color: #e6ebf2;
      color: #475569;
      &:hover:not(.is-disabled) {
        background: #f6f8fb;
        border-color: #cbd5e1;
        color: #1f2937;
      }
    }
    .el-button--primary {
      background-color: #3f65d8;
      border-color: #3f65d8;
      color: #fff;
      &:hover:not(.is-disabled),
      &:focus:not(.is-disabled) {
        background-color: #5078e0;
        border-color: #5078e0;
        color: #fff;
      }
      &:active:not(.is-disabled) {
        background-color: #2c4eb8;
        border-color: #2c4eb8;
        color: #fff;
      }
      &.is-disabled,
      &.is-loading {
        background-color: #a8b8e8;
        border-color: #a8b8e8;
        color: #fff;
      }
    }
  }

  /* 表单 label 字色与全局新风格对齐 */
  .el-form-item__label {
    color: #475569;
    font-weight: 500;
  }

  /* 表单输入控件统一为 32px 高 */
  .el-input--small .el-input__inner,
  .el-select .el-input__inner {
    height: 32px;
    line-height: 32px;
  }

  /* radio 选中态用主色 #3f65d8（覆盖默认 #409EFF） */
  .el-radio__input.is-checked .el-radio__inner {
    background-color: #3f65d8;
    border-color: #3f65d8;
  }
  .el-radio__input.is-checked + .el-radio__label {
    color: #1f2937;
  }
  .el-radio__inner:hover { border-color: #3f65d8; }

  /* checkbox 选中态用主色 */
  .el-checkbox__input.is-checked .el-checkbox__inner,
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: #3f65d8;
    border-color: #3f65d8;
  }
  .el-checkbox__input.is-checked + .el-checkbox__label {
    color: #1f2937;
  }
  .el-checkbox__inner:hover { border-color: #3f65d8; }

  /* 必填星号 */
  .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::before {
    color: #f56c6c;
  }
}
</style>
